import React, { useState, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebaseConfig';
import { signOut } from 'firebase/auth';
import { redirectToCustomerPortal } from '../../api/stripeApi';
import { useTranslation } from 'react-i18next';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';
import { LanguageSelector } from '../LanguageSelector';
import ThemeToggle from '../common/ThemeToggle';
import TenantSwitcher from '../tenant/TenantSwitcher';
import AdvisorContextBar from '../advisor/AdvisorContextBar';
import { BrandLogo } from '../common/BrandLogo';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import {
    IconUser,
    IconLogout,
    IconBilling,
    IconMessages,
    IconAdvisorCockpit,
    IconAdvisorDatev,
} from '../../icons';
import { OnboardingWizard } from '../onboarding';
import AppTour from '../onboarding/AppTour';
import { getPendingInviteToken } from '../../utils/advisorInviteToken';
import { getClientInviteToken } from '../../utils/clientInviteToken';
import { markAdvisorLandingSeen } from '../../utils/advisorLandingSession';
import { ADVISOR_EMAIL_INVITE_ENABLED } from '../../api/advisorInviteApi';
import { useIsAdvisorContext } from '../../hooks/useIsAdvisorContext';
import { canUseMessaging } from '../../config/messagingAccess';
import { canUseKanzleiCockpit } from '../../config/kanzleiAccess';
import { INVOICE_FORM_ROUTE } from '../../config/cockpitAccess';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import KanzleiUebergabeMonthEndBanner from '../premium/KanzleiUebergabeMonthEndBanner';
import AdvisorInviteDashboardCard from '../tenant/AdvisorInviteDashboardCard';
import CaseStudyAskCard from '../tenant/CaseStudyAskCard';
import NavRestructureNotice from './NavRestructureNotice';
import SidebarNav from './SidebarNav';
import MobileTabBar from './MobileTabBar';
import { MobileBottomSheet } from '../common/MobileBottomSheet';
import { canUseMobileTabs } from '../../config/mobileTabsAccess';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import {
    NAV_SECTION_ICONS,
    resolveVisibleTree,
    type NavContext,
    type NavRole,
} from '../../config/navigation';
import { resolveTier } from '../../config/entitlements';
import {
    DesktopNavDropdown,
    DefaultSectionTrigger,
    MobileNavGroup,
    isNavSectionActive,
} from './NavTreeMenus';

/**
 * M-MSG-4: Globaler Ungelesen-Badge fürs Desktop-Nav-Icon. Eigene Komponente
 * (statt `useUnreadCount()` direkt in `MainLayout` zu rufen), damit der
 * `canUseMessaging(effectiveScopeUid)`-Gate am Elternteil auf Komponenten-Ebene
 * bleibt — `useUnreadCount` (und damit sein Firestore-`count()`-Read) wird
 * dadurch nur gemountet, wenn
 * `{canUseMessaging(effectiveScopeUid) && <GlobalUnreadBadge />}` tatsächlich
 * rendert. Ein bedingter Hook-Aufruf direkt in `MainLayout` wäre sowohl ein
 * Rules-of-Hooks-Risiko als auch ein Lint-Fehler.
 */
const GlobalUnreadBadge: React.FC = () => {
    const { t } = useTranslation('common');
    const unread = useUnreadCount();
    if (unread <= 0) return null;
    return (
        <span
            aria-label={t('messaging.newMessagesBadge', { count: unread })}
            className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full
                       bg-indigo-500 text-[#0C1322] text-[10px] font-semibold
                       flex items-center justify-center"
        >
            {unread > 99 ? '99+' : unread}
        </span>
    );
};

/**
 * M-MSG-4: Mobile Nav-Label „Nachrichten (n)" — Suffix-Zähler statt Overlay-Pill
 * (design §1.2). Eigene Komponente aus demselben Grund wie {@link GlobalUnreadBadge}.
 */
const MobileMessagesLabel: React.FC = () => {
    const { t } = useTranslation(['common', 'navigation']);
    const unread = useUnreadCount();
    const label = t('messages', { ns: 'navigation' });
    if (unread <= 0) {
        return <span>{label}</span>;
    }
    return (
        <span aria-label={t('messaging.newMessagesBadge', { count: unread, ns: 'common' })}>
            {label} ({unread > 99 ? '99+' : unread})
        </span>
    );
};

const MainLayout: React.FC = () => {
    const { currentUser, isPremium, sellerData, sellerDataLoading, effectiveScopeUid } = useAuth();
    // M11b-3: Read-only-UX für aktive Steuerberater (advisor). Blendet app-weit
    // die Schreib-Einstiegspunkte aus (kosmetisch über den harten Rules/PHP-Sperren).
    const isReadOnly = useIsReadOnly();
    // fix-advisor-nav-curation: sprechender Alias fürs Nav-Kuratierungs-Signal.
    // Bewusst identisch zu isReadOnly (== activeRole === 'advisor') — siehe
    // architecture-advisor-nav-curation.md Abschnitt A (kein dritter Hook).
    const isAdvisorViewingClient = isReadOnly;
    // M11b Portal-Fix (Bug 1 + Bug 4): SSOT-Erkennung des Steuerberater-Kontexts.
    // Unterdrückt Onboarding-Wizard/AppTour für Berater und steuert die Sichtbarkeit
    // des Owner-Einlade-Menülinks. Bei beiden Flags off zwangsläufig false (inert).
    const isAdvisorContext = useIsAdvisorContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Onboarding Integration - Latching: Einmal geöffnet, bleibt offen bis explizit geschlossen/abgeschlossen
    const [showWizard, setShowWizard] = useState<boolean | null>(null); // null = noch nicht bestimmt
    const [showTour, setShowTour] = useState(false);

    // Einmalige Bestimmung: Soll der Wizard gezeigt werden?
    // Wird nur einmal ausgeführt sobald sellerData geladen ist
    useEffect(() => {
        // Noch am Laden oder bereits bestimmt
        if (sellerDataLoading || showWizard !== null) return;

        // M11b Portal-Fix (Bug 1): Steuerberater bekommen NIE den Owner-Onboarding-Wizard.
        // Kurzschluss VOR jeder hasRequiredFields-Berechnung — ein frischer Berater ohne
        // eigene Firmendaten würde sonst fälschlich den Wizard sehen.
        if (isAdvisorContext) {
            setShowWizard(false);
            return;
        }

        // Onboarding wurde bereits abgeschlossen
        if (sellerData?.onboardingCompleted) {
            setShowWizard(false);
            return;
        }

        // Onboarding-Progress wurde als completed markiert
        if (sellerData?.onboardingProgress?.isCompleted) {
            setShowWizard(false);
            return;
        }

        // Für neue Benutzer: Wizard anzeigen wenn Pflichtfelder fehlen
        // Für bestehende Benutzer mit laufendem Onboarding: Wizard fortsetzen
        const hasOnboardingStarted = (sellerData?.onboardingProgress?.completedSteps?.length ?? 0) > 0;
        const hasRequiredFields = !!(
            sellerData?.name &&
            sellerData?.street &&
            sellerData?.zip &&
            sellerData?.city &&
            sellerData?.country
        );
        
        // Wizard zeigen wenn: Onboarding gestartet aber nicht fertig ODER Pflichtfelder fehlen
        setShowWizard(hasOnboardingStarted || !hasRequiredFields);
    }, [sellerDataLoading, sellerData, showWizard, isAdvisorContext]);

    // Aktiviere Page-View-Tracking für das Dashboard (ohne störende Scroll-Logs)
    useAnalytics({ trackPageViews: true, trackScrollDepth: false });
    const [portalError, setPortalError] = useState<string | null>(null);
    const [isManagingSubscription, setIsManagingSubscription] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Dropdown states
    const [newMenuOpen, setNewMenuOpen] = useState(false);
    // M-UX-2.1 (`plan-ux-2.1-nav-sektionen.md` §5): das Mittel-Band wird
    // generisch über alle Sektionen (außer `actions`/`konto`) gerendert — EIN
    // gemeinsamer "welche Sektion ist offen"-State ersetzt die früheren
    // Boolean-Paare `documentsMenuOpen`/`managementMenuOpen`.
    const [openDesktopSectionId, setOpenDesktopSectionId] = useState<string | null>(null);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);

    // Refs for dropdown menus
    const newMenuRef = useRef<HTMLDivElement>(null);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    // M-UX-2.1: sechs feste Refs (hooks-rule-sicher — die Sektions-IDs sind zur
    // Compile-Zeit bekannt/statisch) + ein Lookup-Record fürs generische
    // Mittel-Band-Rendering (ersetzt `documentsMenuRef`/`managementMenuRef`).
    const verkaufRef = useRef<HTMLDivElement>(null);
    const eingaengeRef = useRef<HTMLDivElement>(null);
    const zahlungenRef = useRef<HTMLDivElement>(null);
    const steuerRef = useRef<HTMLDivElement>(null);
    const stammdatenRef = useRef<HTMLDivElement>(null);
    const steuerberaterRef = useRef<HTMLDivElement>(null);
    const midSectionRefs: Record<string, RefObject<HTMLDivElement | null>> = {
        verkauf: verkaufRef,
        eingaenge: eingaengeRef,
        zahlungen: zahlungenRef,
        steuer: steuerRef,
        stammdaten: stammdatenRef,
        steuerberater: steuerberaterRef,
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (newMenuRef.current && !newMenuRef.current.contains(event.target as Node)) {
                setNewMenuOpen(false);
            }
            // M-UX-2.1: statt zweier fest verdrahteter Ref-Checks (documents/
            // management) schließt dies die aktuell offene Mittel-Band-Sektion,
            // wenn der Klick außerhalb IHRES Refs liegt.
            const openRef = openDesktopSectionId ? midSectionRefs[openDesktopSectionId] : null;
            if (openRef?.current && !openRef.current.contains(event.target as Node)) {
                setOpenDesktopSectionId(null);
            }
            if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
                setAccountMenuOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openDesktopSectionId]);

    // Redirect logic removed in favor of OnboardingWizard
    /* 
    useEffect(() => {
        if (!sellerDataLoading && currentUser) {
            if (
                (!sellerData || 
                !sellerData.name || 
                !sellerData.street || 
                !sellerData.zip || 
                !sellerData.city || 
                !sellerData.country) && 
                !location.pathname.includes('/dashboard/konto')
            ) {
                navigate('/dashboard/konto');
            }
        }
    }, [sellerData, sellerDataLoading, currentUser, navigate, location.pathname]);
    */

    // Close all dropdowns when changing routes
    useEffect(() => {
        setNewMenuOpen(false);
        setOpenDesktopSectionId(null);
        setAccountMenuOpen(false);
    }, [location.pathname]);

    // M11b: Once the user is authenticated in the dashboard, check for a
    // pending advisor invite token (set by RegisterPage/LoginPage when the
    // user arrived via a mail link with ?invite=<token>).
    // If found and we are NOT already on the accept panel, navigate there so
    // the AdvisorInviteAcceptPanel can present the acceptance UI.
    // The redirect fires on every pathname change until either the user
    // reaches the portal page (condition is false there) or the token is
    // cleared after successful acceptance.
    useEffect(() => {
        if (!ADVISOR_EMAIL_INVITE_ENABLED) return;
        if (!currentUser) return;
        const token = getPendingInviteToken();
        if (!token) return;
        if (location.pathname === '/dashboard/konto/steuerberater') return;
        navigate(`/dashboard/konto/steuerberater?invite=${encodeURIComponent(token)}`);
    }, [currentUser, location.pathname, navigate]);

    // WL-K5a: reverse-invite (Kanzlei→Mandant) pending-token redirect. Separate
    // from the advisor redirect above and DELIBERATELY not gated on
    // ADVISOR_EMAIL_INVITE_ENABLED nor on useIsAdvisorContext — the accepting
    // mandant is NOT an advisor (B-E5). Routes the mandant to the client-accept
    // page so they can accept/decline. Fires until the token is cleared on accept.
    useEffect(() => {
        if (!currentUser) return;
        const token = getClientInviteToken();
        if (!token) return;
        if (location.pathname === '/dashboard/konto/kanzlei-zugang') return;
        navigate(`/dashboard/konto/kanzlei-zugang?clientInvite=${encodeURIComponent(token)}`);
    }, [currentUser, location.pathname, navigate]);

    // M-ACF-3 (`architecture-advisor-cockpit-first.md` §3.1 Punkt 3 / §7.5):
    // Session-Einstiegs-Stempel. Bei JEDEM Shell-Mount mit `currentUser` wird der
    // Marker gestempelt (idempotent). Dadurch gilt ein Deep-Link-Einstieg auf eine
    // beliebige NICHT-Index-Route als „Session begonnen" — ein SPÄTERER Klick auf
    // `/dashboard` wird dann nicht mehr ins Cockpit umgebogen (E8). Reihenfolge:
    // der `useState`-Initializer des `AdvisorLandingGate` (Render-Phase) läuft
    // IMMER vor diesem Effekt (Effects laufen nach dem Render) — beim Co-Mount auf
    // `/dashboard` gewinnt also der Gate-Snapshot, der Redirect feuert trotzdem.
    // Helper ist flag-geguarded ⇒ Flag off = no-op (0 Storage-Calls, E1).
    useEffect(() => {
        if (!currentUser) return;
        markAdvisorLandingSeen(currentUser.uid);
    }, [currentUser]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/'); // Redirect to home after logout
        } catch (error) {
            console.error("Logout Error:", error);
        }
    };

    const handleManageSubscription = async () => {
        if (!currentUser || sellerDataLoading) return;

        if (sellerData?.stripeCustomerId) {
            setIsManagingSubscription(true);
            setPortalError(null);
            try {
                const portalUrl = await redirectToCustomerPortal();
                window.location.assign(portalUrl);
            } catch (error: any) {
                console.error("Portal Error:", error);
                setPortalError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.payment.portalFailed' }));
            } finally {
                setIsManagingSubscription(false);
            }
        } else {
            navigate('/dashboard/abonnement');
        }
    };

    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    // M-UX-1.2: NavContext aus den bestehenden Hooks zusammengebaut (§4.1) —
    // dieses Modul reimplementiert KEINE Gates, es füttert nur den bereits
    // vorhandenen Gate-Zustand in die reine `resolveVisibleTree`-Auflösung aus
    // `config/navigation.ts` (W1.1). `role` folgt §2.2: der Mandanten-Modus
    // (isAdvisorViewingClient===true) sieht den Advisor-Baum, der Kanzlei-
    // Kontext (isAdvisorContext, aber NICHT isAdvisorViewingClient) bleibt im
    // Owner-Baum (dessen `konto`-Sektion die Kanzlei-Gates bereits trägt).
    const navCtx: NavContext = {
        uid: currentUser?.uid,
        effectiveScopeUid,
        isPremium,
        // Pricing-Repackaging §3.2: effektiver Tier aus dem vorhandenen
        // `sellerData` abgeleitet (kein AuthContext-Feld) — treibt die
        // Ultra-Gates (Bank/Scan/DATEV) in `resolveVisibleTree`.
        tier: resolveTier(sellerData),
        isAdvisorViewingClient,
        isAdvisorContext,
        sellerData,
    };
    const navRole: NavRole = isAdvisorViewingClient ? 'advisor' : 'owner';
    const navSections = resolveVisibleTree(navCtx, navRole);
    const actionsSection = navSections.find((section) => section.id === 'actions');
    const kontoSection = navSections.find((section) => section.id === 'konto');
    // M-UX-2.1 (§5): generisches Mittel-Band — alle Sektionen außer den zwei
    // bespoke-Blöcken `actions` (+Neu) und `konto` (Avatar-Menü). Ersetzt die
    // vormals hartkodierten `documentsSection`/`managementSection`-`.find(...)`-
    // Aufrufe, die bei umbenannten Sektions-IDs `undefined` zurückgegeben
    // hätten (§0 „zentraler Befund").
    const midSections = navSections.filter((section) => section.id !== 'actions' && section.id !== 'konto');

    // M-UX-3.1 (`plan-ux-3.1-sidebar.md` §3.1): Flag im Komponenten-Body lesen
    // (nicht Modul-Top-Level) — testbar via `process.env`-Set vor `render()`,
    // Muster der `MainLayout.navRenderer.test.tsx`. Fail-closed: default off.
    const uxShellEnabled = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

    // M-UX-6.1 (`plan-ux-6.1-mobile-tabs.md` §3/E1/E6): mobile Bottom-Tab-Bar.
    // Zwei Prädikate:
    //  - `mobileTabsFlags` (nur Flags): steuert die Hamburger-Ausblendung (E6) —
    //    der Hamburger ist bereits `lg:hidden`, ein zusätzlicher `!isDesktop`-JS-
    //    Guard wäre redundant. Flag off ⇒ Hamburger byte-identisch (Null-Delta).
    //  - `mobileTabBarActive` (Flags + `!isDesktop`): steuert Render + Content-
    //    `padding-bottom`. Der JS-Mount-Guard (`!isDesktop`) verhindert, dass der
    //    teure `useInboxNewCount`-Read auf Desktop mountet (CSS-only-Hiding würde
    //    die Komponente dort trotzdem mounten). `isDesktop` == Tailwind `lg`.
    const { isDesktop } = useDeviceDetection();
    // Beta-Unlock: Flag-/Beta-Anteil über den geteilten Helfer (Beta-UID ODER
    // Flag); die `uxShellEnabled`-Kopplung bleibt hier (E1). `currentUser` kann
    // hier noch null sein (Guard folgt unten) ⇒ `?.uid` fail-closed auf Flag.
    const mobileTabsFlags = uxShellEnabled && canUseMobileTabs(currentUser?.uid);
    const mobileTabBarActive = mobileTabsFlags && !isDesktop;

    if (!currentUser) {
        return null; // Or a loading spinner
    }

    // M-UX-6.1b (`plan-ux-6.1b-bottomsheet.md` §1.3): der bisherige „Mehr"-
    // Inhalt (Sprache, Mandanten-Switch, Nachrichten, Nav-Akkordeons, Konto)
    // EXTRAHIERT in eine Variable — Struktur/Klassen 1:1 unverändert. Rendert
    // je nach `mobileTabsFlags` entweder im alten Top-Drawer (Flag off, Null-
    // Delta) oder im neuen `MobileBottomSheet` (Flag on, s. u.). Alle Items
    // schließen weiterhin selbst via `setMobileMenuOpen(false)`.
    const mobileMenuContent = (
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* Mobile: Language Selector */}
            <div className="border-b border-hairline pb-3 mb-2">
                <div className="flex justify-center items-center gap-2">
                    <LanguageSelector />
                    {/* M-D5 W3-1: Theme-Umschalter, self-gated (null bei Flag off). */}
                    <ThemeToggle />
                </div>
                {/* M11 W3: Mandanten-Switch (inert hinter Flag) */}
                <div className="flex justify-center mt-2">
                    <TenantSwitcher />
                </div>
            </div>
            {/* Mobile: Nachrichten — M-MSG-4: rollenunabhängig, nur
                canUseMessaging(effectiveScopeUid)-gegated (NICHT !isReadOnly /
                isAdvisorViewingClient). Zähler-Suffix statt Overlay-Pill. */}
            {canUseMessaging(effectiveScopeUid) && (
                <Link
                    to="/dashboard/nachrichten"
                    className={`flex items-center pl-2 pr-2 py-2 text-sm mb-2 border-b border-hairline pb-3 ${
                        isActive('/dashboard/nachrichten') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <span className="mr-2"><IconMessages /></span>
                    <MobileMessagesLabel />
                </Link>
            )}
            {/* Mobile: WL-K9 Cockpit — Advisor-Kontext + Loop-B-Beta.
                M-UX-3.2: bei `uxShellEnabled` übernimmt die `kanzlei`-
                Sektion im Baum — bare Link unterdrückt. */}
            {!uxShellEnabled && isAdvisorContext && canUseKanzleiCockpit(currentUser?.uid, sellerData) && (
                <Link
                    to="/dashboard/cockpit"
                    className={`flex items-center pl-2 pr-2 py-2 text-sm mb-2 border-b border-hairline pb-3 ${
                        isActive('/dashboard/cockpit') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <span className="mr-2"><IconAdvisorCockpit /></span>
                    {t('advisorCockpit', { ns: 'navigation' })}
                </Link>
            )}
            {/* Mobile: WL-K7 DATEV-Sammelexport — Advisor-Kontext + Loop-B-Beta.
                M-UX-3.2: bare Link bei `uxShellEnabled` unterdrückt (s. o.). */}
            {!uxShellEnabled && isAdvisorContext && canUseKanzleiCockpit(currentUser?.uid, sellerData) && (
                <Link
                    to="/dashboard/datev-sammelexport"
                    className={`flex items-center pl-2 pr-2 py-2 text-sm mb-2 border-b border-hairline pb-3 ${
                        isActive('/dashboard/datev-sammelexport') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <span className="mr-2"><IconAdvisorDatev /></span>
                    {t('advisorDatev', { ns: 'navigation', defaultValue: 'DATEV-Sammelexport' })}
                </Link>
            )}
            {/* Mobile: Neu Gruppe (M-UX-1.2: aus NAV_TREE-Sektion 'actions' — rendert
                nur für role='owner', ersetzt `{!isReadOnly && (...)}`). */}
            {actionsSection && (
                <MobileNavGroup
                    section={actionsSection}
                    ctx={navCtx}
                    groupLabel={t('new', { ns: 'navigation' })}
                    groupIcon={NAV_SECTION_ICONS.actions}
                    onNavigate={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Mobile: M-UX-2.1 (§5) generisches Mittel-Band — dieselbe
                `midSections`-Liste wie beim Desktop-Rendering oben. */}
            {midSections.map((section) => (
                <MobileNavGroup
                    key={section.id}
                    section={section}
                    ctx={navCtx}
                    groupLabel={t(section.labelKey, { ns: 'navigation' })}
                    groupIcon={NAV_SECTION_ICONS[section.id]}
                    onNavigate={() => setMobileMenuOpen(false)}
                />
            ))}

            {/* Mobile: Konto Gruppe (M-UX-1.2: aus NAV_TREE-Sektion 'konto'). Wie
                beim Desktop-Dropdown bleibt `subscriptionUpgrade` aus dem
                generischen Rendering ausgeschlossen — sein Verhalten wird 1:1 vom
                bestehenden !isPremium-Link/isPremium-Button-Paar abgedeckt
                (Bestandsverhalten, unverändert). Kein `border-b` (letzte Gruppe). */}
            {kontoSection && (
                <MobileNavGroup
                    section={kontoSection}
                    ctx={navCtx}
                    groupLabel={t('account', { ns: 'navigation' })}
                    groupIcon={NAV_SECTION_ICONS.konto}
                    onNavigate={() => setMobileMenuOpen(false)}
                    excludeItemIds={['subscriptionUpgrade']}
                    bordered={false}
                    extraItems={
                        <>
                            {!isPremium && (
                                <Link
                                    to="/dashboard/abonnement"
                                    className="flex items-center pl-5 pr-2 py-2 text-sm text-secondary hover:bg-surface-raised"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <span className="mr-2"><IconBilling /></span> {t('subscription', { ns: 'navigation' })}
                                </Link>
                            )}
                            {isPremium && (
                                <button
                                    onClick={() => {
                                        handleManageSubscription();
                                        setMobileMenuOpen(false);
                                    }}
                                    disabled={isManagingSubscription}
                                    className="w-full flex items-center pl-5 pr-2 py-2 text-left text-sm text-secondary hover:bg-surface-raised disabled:opacity-50"
                                >
                                    <span className="mr-2"><IconBilling /></span>
                                    {isManagingSubscription ? t('loading', { ns: 'common' }) : t('manageSubscription', { ns: 'navigation' })}
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    handleLogout();
                                    setMobileMenuOpen(false);
                                }}
                                className="w-full flex items-center pl-5 pr-2 py-2 text-left text-sm text-secondary hover:bg-surface-raised"
                            >
                                <span className="mr-2"><IconLogout /></span>
                                {t('logout', { ns: 'navigation' })}
                            </button>
                        </>
                    }
                />
            )}
        </div>
    );

    const content = (
        <>
            {/* M-UX-7.1 W7 Design-Polish (sticky-as-unit): Top-Bar UND
                Advisor-Kontext-Leiste teilen sich EINEN `sticky top-0 z-50`-
                Wrapper, statt dass der Header allein haftet und die Leiste im
                Fluss darunter mitscrollt (Vorgaenger-Auslassung). So bleibt die
                Leiste beim Scrollen buendig unter der Top-Bar sichtbar — ohne
                hartkodierten `top`-Offset und ohne dynamische Header-Hoehen-
                Mathematik bei Mobile-Umbruch. Im Owner-/Flag-off-Modus rendert
                die Leiste `null` ⇒ der Wrapper haftet nur den Header (0 Layout-
                Delta ggue. dem frueheren „Header ist selbst sticky"). */}
            <div className="sticky top-0 z-50">
            <header className="bg-surface bg-opacity-90 backdrop-blur-sm border-b border-hairline text-primary">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            {/* M-UX-Cockpit-Home (`plan-ux-table-cockpit-quality.md` §D.2):
                                Logo = Home-Link (Marktkonvention). UNCONDITIONAL, kein
                                Flag-Gate — bei Flag OFF ist `/dashboard` das Rechnungs-
                                formular (heutige Startseite), bei Flag ON das Cockpit;
                                der Link ist in beiden Welten korrekt. */}
                            <Link to="/dashboard" aria-label={t('goToOverview', { ns: 'navigation', defaultValue: 'Zur Übersicht' })}>
                                <BrandLogo className="h-8 sm:h-10" mode="solo" />
                            </Link>
                        </div>

                        {/* Desktop Navigation with Dropdown Menus */}
                        <div className="hidden lg:flex items-center space-x-6">
                            {/* Language Selector */}
                            <LanguageSelector />
                            {/* M-D5 W3-1: Theme-Umschalter, self-gated (null bei Flag off REACT_APP_UX_THEME_V5_ENABLED). */}
                            <ThemeToggle />
                            {/* M11 W3: Mandanten-Switch (inert hinter Flag; rendert null bei Flag off / nur eigenem Tenant) */}
                            <TenantSwitcher />
                            {/* M-MSG-4: Nachrichten — rollenunabhängig sichtbar (owner UND advisor),
                                NUR hinter canUseMessaging(effectiveScopeUid) gegated (Beta-Tenant-Allowlist
                                ODER Flag). Bewusst NICHT unter !isReadOnly / isAdvisorViewingClient
                                geklammert (Kernnutzen ist der Berater-Zugang). */}
                            {canUseMessaging(effectiveScopeUid) && (
                                <Link
                                    to="/dashboard/nachrichten"
                                    className={`relative flex items-center text-sm font-medium ${
                                        isActive('/dashboard/nachrichten') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                                    } transition`}
                                >
                                    <span className="mr-1"><IconMessages /></span>
                                    <span>{t('messages', { ns: 'navigation' })}</span>
                                    <GlobalUnreadBadge />
                                </Link>
                            )}
                            {/* WL-K9: Exception-First-Cockpit — nur im Advisor-Kontext und
                                Loop-B-Beta (rein kosmetisch; der Server erzwingt Scope+Beta).
                                M-UX-3.2: bei `uxShellEnabled` übernimmt die `kanzlei`-Sektion
                                im Baum (`config/navigation.ts`) — bare Link unterdrückt. */}
                            {!uxShellEnabled && isAdvisorContext && canUseKanzleiCockpit(currentUser?.uid, sellerData) && (
                                <Link
                                    to="/dashboard/cockpit"
                                    className={`flex items-center text-sm font-medium ${
                                        isActive('/dashboard/cockpit') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                                    } transition`}
                                >
                                    <span className="mr-1"><IconAdvisorCockpit /></span>
                                    <span>{t('advisorCockpit', { ns: 'navigation' })}</span>
                                </Link>
                            )}
                            {/* WL-K7: DATEV-Sammelexport — Advisor-Kontext + Loop-B-Beta
                                (rein kosmetisch; der Server erzwingt Scope+Beta pro Mandant).
                                M-UX-3.2: bare Link bei `uxShellEnabled` unterdrückt (s. o.). */}
                            {!uxShellEnabled && isAdvisorContext && canUseKanzleiCockpit(currentUser?.uid, sellerData) && (
                                <Link
                                    to="/dashboard/datev-sammelexport"
                                    className={`flex items-center text-sm font-medium ${
                                        isActive('/dashboard/datev-sammelexport') ? 'text-accent-ink' : 'text-secondary hover:text-primary'
                                    } transition`}
                                >
                                    <span className="mr-1"><IconAdvisorDatev /></span>
                                    <span>{t('advisorDatev', { ns: 'navigation', defaultValue: 'DATEV-Sammelexport' })}</span>
                                </Link>
                            )}
                            {/* Neu Dropdown (M-UX-1.2: aus NAV_TREE-Sektion 'actions' — rendert nur
                                für role='owner', da die Sektion `roles: ['owner']` trägt; ersetzt
                                das bisherige `{!isReadOnly && (...)}`). */}
                            {actionsSection && (
                                <DesktopNavDropdown
                                    section={actionsSection}
                                    ctx={navCtx}
                                    isOpen={newMenuOpen}
                                    onToggle={() => setNewMenuOpen(!newMenuOpen)}
                                    menuRef={newMenuRef}
                                    triggerContent={
                                        <DefaultSectionTrigger icon={NAV_SECTION_ICONS.actions} label={t('new', { ns: 'navigation' })} />
                                    }
                                    isTriggerActive={isNavSectionActive(location.pathname, actionsSection.items, navCtx)}
                                    panelWidthClassName="w-56"
                                />
                            )}

                            {/* M-UX-2.1 (§5): generisches Mittel-Band-Rendering — EIN `.map` über
                                alle Sektionen außer `actions`/`konto`, statt der vormals zwei
                                hartkodierten Dropdowns (Dokumente/Verwaltung). Neue Sektionen aus
                                `config/navigation.ts` erscheinen dadurch automatisch, ohne dass
                                dieser Consumer je wieder literal auf Sektions-IDs verdrahtet werden muss. */}
                            {/* M-UX-3.1 (§3.2): bei Flag ON übernimmt die persistente
                                `SidebarNav`-Spalte (§3.3) diese sechs Sektionen — die
                                Top-Bar-Dropdown-Trigger entfallen dann, um Doppel-Navigation
                                zu vermeiden. Bei Flag OFF (`uxShellEnabled===false`) ist
                                `!uxShellEnabled` `true`, also `true && expr` → `expr`: exakt
                                das heutige `{midSections.map(...)}`-Rendering, Null-Delta. */}
                            {!uxShellEnabled && midSections.map((section) => (
                                <DesktopNavDropdown
                                    key={section.id}
                                    section={section}
                                    ctx={navCtx}
                                    isOpen={openDesktopSectionId === section.id}
                                    onToggle={() =>
                                        setOpenDesktopSectionId((prev) => (prev === section.id ? null : section.id))
                                    }
                                    menuRef={midSectionRefs[section.id]}
                                    triggerContent={
                                        <DefaultSectionTrigger
                                            icon={NAV_SECTION_ICONS[section.id]}
                                            label={t(section.labelKey, { ns: 'navigation' })}
                                        />
                                    }
                                    isTriggerActive={isNavSectionActive(location.pathname, section.items, navCtx)}
                                />
                            ))}

                            {/* Konto Dropdown (M-UX-1.2: aus NAV_TREE-Sektion 'konto'). Der Avatar-
                                Trigger bleibt Custom-JSX (kein generischer Section-Trigger). Das Item
                                `subscriptionUpgrade` wird bewusst aus dem generischen Rendering
                                ausgeschlossen (`excludeItemIds`) — sein Verhalten wird 1:1 vom
                                bestehenden `handleManageSubscription`-Button abgedeckt (Desktop:
                                unconditional, Label/Ziel abhängig von `stripeCustomerId`; s.
                                `config/navigation.ts`-Modulkopf zum „Desktop-Kombi-Button, der
                                bewusst NICHT als NavItem geführt wird"). Logout ist ebenfalls kein
                                NavItem (Button, kein Routen-Ziel) — beide bleiben `extraItems`. */}
                            {kontoSection && (
                                <DesktopNavDropdown
                                    section={kontoSection}
                                    ctx={navCtx}
                                    isOpen={accountMenuOpen}
                                    onToggle={() => setAccountMenuOpen(!accountMenuOpen)}
                                    menuRef={accountMenuRef}
                                    align="right"
                                    panelWidthClassName="w-56"
                                    excludeItemIds={['subscriptionUpgrade']}
                                    highlightActiveItems
                                    isTriggerActive={isNavSectionActive(location.pathname, kontoSection.items, navCtx)}
                                    triggerContent={
                                        <>
                                            {currentUser?.photoURL ? (
                                                <img
                                                    src={currentUser.photoURL}
                                                    alt="User Avatar"
                                                    className="w-8 h-8 rounded-full mr-2 border-2 border-hairline group-hover:border-indigo-500 transition"
                                                />
                                            ) : (
                                                <span className="w-8 h-8 rounded-full mr-2 border-2 border-hairline group-hover:border-indigo-500 transition flex items-center justify-center bg-surface-inset">
                                                    <span className="text-muted">
                                                        <IconUser />
                                                    </span>
                                                </span>
                                            )}
                                            <span>{currentUser?.displayName || currentUser?.email}</span>
                                        </>
                                    }
                                    extraItems={
                                        <>
                                            <button
                                                onClick={handleManageSubscription}
                                                disabled={isManagingSubscription || sellerDataLoading}
                                                className="flex items-center w-full px-4 py-2 text-sm text-secondary hover:bg-surface-inset hover:text-primary disabled:opacity-50"
                                                role="menuitem"
                                            >
                                                <span className="mr-2"><IconBilling /></span>
                                                {sellerDataLoading ? t('loading', { ns: 'common' }) : (sellerData?.stripeCustomerId ? t('manageSubscription', { ns: 'navigation' }) : t('subscribePremium', { ns: 'navigation' }))}
                                            </button>

                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center w-full text-left px-4 py-2 text-sm text-secondary hover:bg-surface-inset hover:text-primary"
                                                role="menuitem"
                                            >
                                                <span className="mr-2"><IconLogout /></span> {t('logout', { ns: 'navigation' })}
                                            </button>
                                        </>
                                    }
                                />
                            )}
                        </div>
                        
                        {/* Mobile Menu Button */}
                        {/* M-UX-6.1 (E6): bei aktiver Tab-Bar (`mobileTabsFlags`)
                            wird der Hamburger ausgeblendet — der „Mehr"-Slot der
                            Bar ist dann der einzige Drawer-Einstieg. Prädikat
                            bewusst OHNE `!isDesktop` (der Block ist schon
                            `lg:hidden`). Flag off ⇒ `!mobileTabsFlags` true ⇒
                            byte-identisch zu heute. */}
                        {!mobileTabsFlags && (
                        <div className="lg:hidden">
                            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-secondary hover:text-primary focus:outline-none">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    {mobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                                    )}
                                </svg>
                            </button>
                        </div>
                        )}
                    </div>
                    
                    {/* Mobile Menu with Collapsible Sections */}
                    {/* fix/ux-shell-scroll: der Container steckt im position:sticky
                        Header-Wrapper (:353) — ein gepinntes Element scrollt bei
                        Überlänge NIE mit dem Dokument, darum eigener Scrollport
                        via fb-mobile-menu-scroll (plan-ux-shell-scroll.md §0.1/§3).
                        M-UX-6.1b (`plan-ux-6.1b-bottomsheet.md` §1.3): dieser
                        Top-Drawer ist NUR NOCH der Flag-off-Pfad — bei aktiver
                        Tab-Bar (`mobileTabsFlags`) rendert derselbe
                        `mobileMenuContent` stattdessen im `MobileBottomSheet`
                        weiter unten im Fragment (neben `MobileTabBar`). Null-
                        Delta-Invariante bei Flag off, s.
                        `MainLayout.mobileTabs.flagOff.test.tsx`. */}
                    {mobileMenuOpen && !mobileTabsFlags && (
                        <div className="lg:hidden bg-surface-raised border-t border-hairline fb-mobile-menu-scroll fb-thin-scrollbar">
                            {mobileMenuContent}
                        </div>
                    )}
                </div>
            </header>

            {/* M-UX-7.1 (`plan-ux-7.1-advisor-shell.md` §5): persistente Amber-
                Kontext-Leiste, nur bei Flag ON + aktivem Advisor-Lesemodus.
                Liegt IM gemeinsamen sticky-Wrapper mit dem Header (W7 Design-
                Polish), damit beide als Einheit oben haften. Flag OFF/Owner ⇒
                `null`, der Wrapper haftet dann nur den Header (Null-Delta). */}
            {uxShellEnabled && isAdvisorViewingClient && <AdvisorContextBar />}
            </div>

            {/* M-UX-2.1 (§4): dismissbarer "Neue Menüstruktur"-Hinweis — self-contained
                (eigener localStorage-Dismiss-State), unconditional gerendert wie die
                Banner unten (Outer/Inner-Gate-Muster: die Komponente selbst entscheidet
                über `null` vs. sichtbar). */}
            <NavRestructureNotice />

            {/* M-UX-6.1 (§2.4/§3.3): Content-Freiraum, damit die fixe Bottom-Tab-
                Bar den Seiteninhalt nicht überdeckt — NUR wenn die Bar rendert
                (gleiches E1-Prädikat), sonst 0-Delta (leere Klasse). */}
            <main className={`flex-grow${mobileTabBarActive ? ' fb-has-mobile-tabbar' : ''}`}>
                <div className="container mx-auto p-4 md:p-6 lg:p-8">
                    {portalError && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
                            {portalError}
                        </div>
                    )}
                    {/* WL-F1 Premium-Trigger 4 "Kanzlei-Übergabe" — Monatsende-Banner.
                        Selbst-gegated (Beta/Premium/Scope/Fenster/Cooldown intern in der
                        Komponente) — hier bewusst unconditional gerendert (Outer/Inner-Gate,
                        Modul-Kopf), damit MainLayout keine zusätzliche Bedingung pflegen muss. */}
                    <KanzleiUebergabeMonthEndBanner />
                    {/* WL-GTM-7 (a) "Dashboard-Karte" + Case-Study-Engine-Hook — beide
                        selbst-gegated (Outer/Inner-Gate, Modul-Kopf), unconditional
                        gerendert wie der Banner oben. */}
                    <AdvisorInviteDashboardCard />
                    <CaseStudyAskCard />
                    <Outlet />
                </div>
            </main>

            {/* Onboarding Wizard - Latching: bleibt offen bis explizit geschlossen.
                M-UX-G1: Primär-CTA führt aktiv zum Aha-Moment (erste Rechnung) —
                explizite Navigation zum Rechnungs-Erstellformular (M-UX-4.2:
                INVOICE_FORM_ROUTE statt hartem '/dashboard' — Flag off identisch),
                statt sich auf die zufällig darunterliegende Route zu verlassen. */}
            {showWizard && (
                <OnboardingWizard
                    onClose={(startTour) => {
                        setShowWizard(false);
                        if (startTour) {
                            setShowTour(true);
                        } else {
                            navigate(INVOICE_FORM_ROUTE);
                        }
                    }}
                />
            )}

            {/* App Tour — M11b Portal-Fix (Bug 1): für Berater defensiv unterdrückt. */}
            {showTour && !isAdvisorContext && (
                <AppTour onComplete={() => setShowTour(false)} />
            )}

            <footer className="bg-surface text-primary py-6 text-sm">
                <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0">
                    <p className="text-muted">&copy; {new Date().getFullYear()} FaeBill. {t('allRightsReserved', { ns: 'legal' })}.</p>
                    <div className="space-x-4">
                        <a href={`${process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'}/impressum`} className="text-muted hover:text-primary transition">{t('imprint', { ns: 'legal' })}</a>
                        <a href={`${process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'}/datenschutz`} className="text-muted hover:text-primary transition">{t('privacyPolicy', { ns: 'legal' })}</a>
                        <a href={`${process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'}/agb`} className="text-muted hover:text-primary transition">{t('termsOfService', { ns: 'legal' })}</a>
                    </div>
                </div>
            </footer>

            {/* M-UX-6.1 (§3 Eingriff 2): mobile Bottom-Tab-Bar am Ende des
                content-Fragments. Render-Guard = `mobileTabBarActive`
                (Flags + `!isDesktop`, E1) — kein Desktop-Mount des teuren
                `useInboxNewCount`-Reads; CSS `lg:hidden` in der Komponente als
                Belt-and-Braces. Flag off ⇒ nichts im DOM (Null-Delta). */}
            {mobileTabBarActive && (
                <MobileTabBar
                    ctx={navCtx}
                    navRole={navRole}
                    onOpenMore={() => setMobileMenuOpen(true)}
                />
            )}

            {/* M-UX-6.1b (`plan-ux-6.1b-bottomsheet.md` §1.3): bei aktiver Tab-Bar
                übernimmt das Bottom-Sheet den „Mehr"-Inhalt (`mobileMenuContent`,
                s. o.) statt des Top-Drawers im Header — reitet auf demselben
                `mobileTabsFlags`-Gate wie die Bar selbst (§1.4, kein neues Flag).
                Portal-basiert (`document.body`), entkommt daher unabhängig vom
                `sticky top-0`-Header-Wrapper; Platzierung hier ist nur Render-
                Reihenfolge im JSX, kein DOM-Nesting-Erfordernis. */}
            {mobileTabsFlags && (
                <MobileBottomSheet
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    title={t('mobileTabs.more', { ns: 'common' })}
                >
                    {mobileMenuContent}
                </MobileBottomSheet>
            )}
        </>
    );

    // M-UX-3.1 (§3.3): Shell-Switch. Flag OFF rendert exakt den heutigen Root
    // (min-h-screen bg-surface flex flex-col, siehe unten,
    // byte-identisch, kein zusätzlicher Wrapper). Flag ON + ≥lg fügt eine
    // persistente Sidebar-Spalte links vom Content hinzu; `<lg` kollabiert die
    // Sidebar (`hidden lg:flex`), der Hamburger-/Mobile-Pfad im `<header>`
    // bleibt in beiden Fällen unverändert (§3.4).
    // M-UX-7.1 (§4.1): `data-mode="advisor"` nur bei Flag ON + aktivem
    // Advisor-Lesemodus. `undefined` ⇒ React rendert das Attribut nicht ⇒
    // Owner-/Flag-off-DOM bleibt byte-identisch (Null-Delta-Invariante §7).
    const dataMode = uxShellEnabled && isAdvisorViewingClient ? 'advisor' : undefined;

    if (uxShellEnabled) {
        return (
            <div className="min-h-screen bg-surface lg:flex" data-mode={dataMode}>
                <SidebarNav
                    sections={midSections}
                    ctx={navCtx}
                    isPremium={isPremium}
                    className="hidden lg:flex lg:flex-col lg:w-64 shrink-0 border-r border-hairline lg:sticky lg:top-0 lg:h-screen"
                />
                <div className="flex-1 flex flex-col min-w-0">{content}</div>
            </div>
        );
    }

    return <div className="min-h-screen bg-surface flex flex-col" data-mode={dataMode}>{content}</div>;
};

export default MainLayout;